import { booleanAttribute } from "#ce/utils/props";

export const props = Object.freeze({
  /**
   * URL to POST the preview request to. The endpoint starts the preview in the
   * background and returns a preview token.
   * @type {String}
   */
  previewUrl: {
    type: String,
    required: true,
  },
  /**
   * URL to GET the preview status from, with the preview token as a `token`
   * query param. Returns the preview rows once the background run is ready.
   * @type {String}
   */
  statusUrl: {
    type: String,
    required: true,
  },
  /**
   * URL to POST a preview cancellation to, with the preview token in the body;
   * the server stops the background run.
   * @type {String}
   */
  cancelUrl: {
    type: String,
    required: true,
  },
  /**
   * URL to POST the confirmed assignment to; the server queues the import.
   * @type {String}
   */
  processUrl: {
    type: String,
    required: true,
  },
  /**
   * The client's display name (e.g. "Advocate Health"). Used in the
   * client-control flag label and the outcome column text.
   * @type {String}
   */
  clientName: {
    type: String,
  },
  /**
   * JSON array of `{title, value}` option objects for the specialty
   * multi-select. Attributes arrive as strings, so this is parsed in the store.
   * @type {String}
   */
  specialties: {
    type: String,
  },
  /**
   * HTML content for the dropzone instruction text.
   * @type {String}
   */
  dropzoneText: {
    type: String,
  },
  /**
   * Server-rendered HTML for the file specification panel.
   * @type {String}
   */
  fileSpecHtml: {
    type: String,
  },
  /**
   * Label for the term tag input field.
   * @type {String}
   */
  termInputLabel: {
    type: String,
  },
  /**
   * Label for the "add" assignment mode option.
   * @type {String}
   */
  modeAddLabel: {
    type: String,
  },
  /**
   * Label for the "replace" assignment mode option.
   * @type {String}
   */
  modeReplaceLabel: {
    type: String,
  },
  /**
   * Label for the assignment mode select.
   * @type {String}
   */
  modeLabel: {
    type: String,
  },
  /**
   * Label for the specialty multi-select.
   * @type {String}
   */
  specialtySelectLabel: {
    type: String,
  },
  /**
   * Label for the client-control flag toggle. The `{client}` token is
   * substituted with `clientName`.
   * @type {String}
   */
  flagToggleLabel: {
    type: String,
  },
  /**
   * Whether this deployment manages client-controlled specialties (canonical
   * MST management). The helper sets this as a bare present-or-absent
   * attribute. When falsy, the client-control toggle, the preview's Control
   * column, and the will-enable summary fragment are all omitted, and
   * `enable_client_specialties` is always submitted as "false".
   * @type {Boolean|String}
   */
  clientControlEnabled: booleanAttribute(),
  /**
   * JSON object of `{new, existing}` tooltip strings for the preview's row
   * status icons (server passes localized text). English defaults apply when
   * omitted. Error rows show the joined row errors instead.
   * @type {String}
   */
  statusLabels: {
    type: String,
  },
  /**
   * JSON object of `{will_enable, unchanged, new_term}` display strings for
   * the preview's Control column (server passes localized text). English
   * defaults using `clientName` apply when omitted.
   * @type {String}
   */
  outcomeLabels: {
    type: String,
  },
  /**
   * JSON object of localized UI copy, always passed by the helper. Deep-merged
   * over minimal English defaults in the store. Shape (camelCase keys):
   * `{preview, back, confirm, cancelPreview, viewImport, fileErrors, orDivider,
   * termInputPlaceholder, invalidEntry, removedSpecialty, loading,
   * termsEntered: {one, other},
   * progress: {rows, remaining, minutes: {one, other}, seconds: {one, other}},
   * columns: {status, name, type, resultingSpecialties, control},
   * errors: {unsupportedFile, noTerms, noSpecialties, previewFailed,
   * previewInterrupted, processFailed},
   * summary: {new, existing, willEnable, errors},
   * flash: {queued}}`
   * — the summary/flash/termsEntered values are `{one, other}` templates with a
   * literal `%{count}` placeholder (willEnable arrives with the client name
   * already interpolated); `removedSpecialty` uses `%{name}`; `progress.rows`
   * uses `%{processed}` and `%{total}`, `progress.remaining` uses `%{time}`.
   * @type {String}
   */
  labels: {
    type: String,
  },
  /**
   * Title for the modal.
   * @type {String}
   */
  modalTitle: {
    type: String,
  },
  /**
   * Whether the modal should auto-open on mount.
   * @type {Boolean|String}
   */
  autoOpen: booleanAttribute(),
});
